'use client'

/**
 * Documentation section for /products/3m-pwf-colour-series: the three official
 * 3M PDFs sourced via Firecrawl web search + scrape 2026-08-25 from
 * multimedia.3m.com (scripts/supplier-assets/intake-pwf-docs/), converted to
 * HTML rather than embedded as PDFs or offered as download-only links.
 * Follows the pattern established in 3m-2080.tsx / avery-sw900.tsx /
 * hexis-bodyfence.tsx. No colour swatch poster was supplied for this pass, so
 * this file has no poster/Modal image section, matching hexis-bodyfence.tsx.
 *
 * - Product Bulletin (Rev A): transcribed from a Firecrawl PDF-parse text
 *   extraction (pwf-product-bulletin.txt), in the PDF's own section order.
 * - Installation Guide (Revision A, April 2024): transcribed from
 *   pwf-installation-guide.txt. IMPORTANT: pages 2-3 of the source PDF (the
 *   numbered "Installation Instructions" list, steps 2-9, and part of the
 *   "Maintenance, Cleaning and Removal" section) extracted as garbled text, a
 *   font-substitution/encoding artifact of the PDF, not real content. That
 *   range was excluded from the intake file rather than guessed at, and is
 *   likewise not fabricated here: only step 1 and steps 10-13 of the
 *   Installation Instructions are shown, with a note in the component itself
 *   (matching the note already in the intake file) explaining that steps 2-9
 *   were not recoverable from the source PDF's text layer.
 * - Warranty Card (Vehicle Owner Limited Warranty): transcribed from
 *   pwf-warranty-card.txt.
 *
 * See scripts/supplier-assets/intake-pwf-docs/sources.json for full
 * provenance, source URLs, and authorization basis.
 *
 * Symbol restoration: the source text files use plain-text placeholder
 * markers for trademark/registered/dagger symbols, an artifact of the PDF
 * text-layer extraction, not a content change. Restored throughout as the
 * proper Unicode characters:
 * - "(TM)" -> "™" (e.g. "3M(TM) Protection Wrap Film" -> "3M™ Protection Wrap
 *   Film", "Controltac(TM)" -> "Controltac™")
 * - "(R)" -> "®" (e.g. "WrapGlove(R)" -> "WrapGlove®", "Scotch-Brite(R)" ->
 *   "Scotch-Brite®", "Meguiar's(R)" -> "Meguiar's®", "Mirror Glaze(R)" ->
 *   "Mirror Glaze®")
 * - "(dagger)" -> "†" (the two warranty-length footnote markers in the
 *   Product Bulletin's feature list)
 *
 * No em-dash (U+2014) or en-dash (U+2013) was found anywhere in any of the 3
 * source files (checked by grepping all three for both characters), so no
 * house-style dash substitution was needed in this file.
 *
 * Every figure, unit, and claim is copied exactly from the source text files.
 * No figure, unit, or word was otherwise added, removed, or rounded. The
 * Product Bulletin's own "Product Description" heading has no body text in
 * the source (the PDF's extraction places it as an empty heading directly
 * followed by "Product Features"), so it is not reproduced as its own
 * DocSection here; the Product Features list that immediately follows it in
 * the source is shown under a single "Product Description & Features"
 * heading, matching how 3m-2080.tsx pairs the same two headings.
 *
 * Duplicate omissions (same restraint as 3m-2080.tsx/avery-sw900.tsx):
 * - The Installation Guide's closing "Warranty, Limited Remedy, and
 *   Disclaimer" heading is explicitly flagged in the source intake file as
 *   identical boilerplate to the Product Bulletin's own version of that
 *   section; it is kept once, in the Product Bulletin group, and skipped in
 *   the Installation Guide group.
 * - The Installation Guide's "Health and Safety" section duplicates the
 *   Product Bulletin's "Health and Safety" section (both instruct reading
 *   manufacturer labels/SDS sheets and following equipment instructions,
 *   just in reversed sentence order). The Product Bulletin's version is kept
 *   as canonical (it names the 3M.com/SDS link); the Installation Guide's
 *   restatement is skipped.
 * - Both the Product Bulletin and the Installation Guide describe the same
 *   "textured, metallic, pearl, and color flip films may have a directional
 *   characteristic" guidance and the same lot-to-lot color variation
 *   guidance is present only in the Installation Guide (the Bulletin does
 *   not repeat it), so both are kept once each, in the Installation Guide's
 *   Vehicle Preparation section where they were found, with no duplication
 *   across sections.
 * - Application temperature is stated in both the Product Bulletin
 *   (Application Characteristics table: 60F to 85F, optimal 65F to 75F) and
 *   implicitly assumed in the Installation Guide (which gives no separate
 *   temperature figure of its own beyond the 200F-220F heat-gun setting for
 *   fixing adhesive markings, a distinct figure for a distinct purpose, so
 *   nothing was dropped there).
 * - The Warranty Card's "By entering the information into 3M's warranty
 *   registration system..." Carfax-sharing consent notice is kept once, in
 *   the Warranty section's Exclusions subsection where the source places it,
 *   and is not a duplicate of anything elsewhere in the three documents.
 *
 * The Installation Guide's numbered "Cleaning Instructions" list keeps its
 * source numbering (1, 3-6); step 2 (described only as "(adhesive remover
 * step -- not cleanly extracted)" in the intake file) is omitted rather than
 * guessed at, with a note.
 */
import { Disclosure, DisclosureGroup } from '@heroui/react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'

// ---------------------------------------------------------------------
// Presentational helpers (same shape as 3m-2080.tsx / avery-sw900.tsx /
// hexis-bodyfence.tsx)
// ---------------------------------------------------------------------

function KeyValueTable({ rows }: { rows: Array<{ label: string; value: ReactNode }> }) {
  return (
    <div className='rounded-lg border border-divider overflow-hidden'>
      {rows.map((row, i) => (
        <div
          key={row.label}
          className={`flex flex-col gap-1 px-4 py-2.5 text-sm sm:flex-row sm:gap-6 ${i % 2 === 0 ? 'bg-overlay' : ''}`}
        >
          <span className='text-muted font-medium shrink-0 sm:w-2/5'>{row.label}</span>
          <span className='text-foreground'>{row.value}</span>
        </div>
      ))}
    </div>
  )
}

function BulletList({ items }: { items: ReactNode[] }) {
  return (
    <ul className='list-disc space-y-1.5 pl-5 text-foreground/80'>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

interface DocSectionProps {
  id: string
  title: string
  children: ReactNode
}

function DocSection({ id, title, children }: DocSectionProps) {
  return (
    <Disclosure id={id} aria-label={title} className='overflow-hidden rounded-lg border border-divider bg-surface'>
      <Disclosure.Heading>
        {/* transform-none: HeroUI scales a pressed button to 0.97, which on a
            full-width trigger inside an overflow-hidden card leaves a sliver of
            card showing around the pressed fill. cw-ui-heading: the trigger
            sits in the Disclosure's h3, which the theme sets in Anton; the
            title stays in the body face (spec 3.2 component-heading opt-out). */}
        <Button
          slot='trigger'
          variant='ghost'
          className='h-auto w-full justify-between rounded-none px-4 py-3.5 text-left transform-none'
        >
          <span className='cw-ui-heading text-base font-semibold text-foreground'>{title}</span>
          <Disclosure.Indicator className='text-muted' />
        </Button>
      </Disclosure.Heading>
      <Disclosure.Content>
        <Disclosure.Body className='space-y-3 border-t border-divider px-4 py-4 text-sm text-foreground/80'>
          {children}
        </Disclosure.Body>
      </Disclosure.Content>
    </Disclosure>
  )
}

// ---------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------

export function PwfColourSeriesDocs() {
  return (
    <div className='space-y-8'>
      {/* Product Bulletin */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Product Bulletin</h3>
        <p className='text-xs text-muted mb-3'>3M™ Protection Wrap Film Product Bulletin, Revision A</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='pb-description-features' title='Product Description &amp; Features'>
            <BulletList
              items={[
                '3M™ Protection Wrap Film combines vehicle customization with durable protection against rock chips, scratches and stains',
                'Create full color change or personalize with accents or partial wraps with nearly 25 premium colors, including gloss and matte finishes, flips and textures',
                'At least 70% thicker than typical vinyl films, providing long-lasting protection',
                'Mirror-like finish that looks like paint',
                "Dry installation method that's two times faster end to end than wet-applied PPF*",
                'Wet installation method that allows for the repositioning, precise alignment, and workflow familiar to paint protection film installers',
                'Both the wet and dry method can be used in a single installation, allowing installers to customize their installation experience based on the material, surface, and installation environment',
                'Cold stretch capabilities make it highly conformable in deep recesses with minimal heat',
                <>
                  3M™ Controltac™ Film Technology and 3M™ Comply™ Adhesive Technology
                  <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                    <li>
                      3M™ Controltac™ Film Technology minimizes the adhesive&rsquo;s initial contact area for easy
                      sliding, tacking, snap-up, and repositioning.
                    </li>
                    <li>
                      3M™ Comply™ Adhesive Technology allows air to exit through air release channels to the edge of
                      the film for fast, easy, bubble-free installations.
                    </li>
                  </ul>
                </>,
                'Self-healing polymer technology allows minor scratches to disappear with heat applied to the film',
                'Approved for use with 3M™ Knifeless™ Tape Precision Line and Design Line (for dry installation method only)',
                '10-year consumer warranty for vehicle applications†',
                '3-year consumer warranty for watercraft applications†'
              ]}
            />
            <p className='text-xs text-muted'>* When compared to Scotchgard™ Paint Protection Film Pro Series 200.</p>
            <p className='text-xs text-muted'>† Ask a 3M authorized installer or see the warranty card for complete details.</p>
            <p className='text-xs text-muted'>NOTE: All installations should be conducted by a professional installer.</p>
            <p className='text-xs text-muted'>NOTE: Mist the liner and cap sheet with water to help reduce static.</p>
          </DocSection>

          <DocSection id='pb-recommended-uses' title='Recommended Types of Films and End Uses'>
            <BulletList
              items={[
                'Standard vehicle films, including solid color, detailing, decoration',
                'Personal watercraft (when applied above the static water line)'
              ]}
            />
          </DocSection>

          <DocSection id='pb-coverage-options' title='Coverage Options'>
            <KeyValueTable
              rows={[
                { label: 'Accents', value: 'Gas cap, chrome rocker panels, stripes, delete, racing etc.' },
                { label: 'Partial Coverage', value: 'Hood or roof examples' },
                { label: 'Full Vehicle Coverage', value: 'Complete color change' },
                { label: 'Watercraft Coverage', value: 'Above the static water line' }
              ]}
            />
          </DocSection>

          <DocSection id='pb-physical-characteristics' title='Physical Characteristics'>
            <p className='text-xs text-muted'>
              The following technical information and data should be considered representative or typical only and
              should not be used for specification purposes.
            </p>
            <KeyValueTable
              rows={[
                { label: 'Material', value: 'Polyurethane' },
                {
                  label: 'Thickness',
                  value: (
                    <>
                      Film with adhesive: 7.5 mil to 9 mil (0.19 mm to 0.23 mm), depending on color.
                      <br />
                      Release liner: 6.4 mil (0.16 mm).
                      <br />
                      Cap sheet: 2.0 mil (0.05 mm)
                    </>
                  )
                },
                { label: 'Adhesive', value: 'Pressure-sensitive adhesive, repositionable with air release channels' },
                { label: 'Adhesive Color', value: 'Clear' },
                { label: 'Typical Adhesion 20 Minutes After Application', value: 'Painted panel: 2.1 lbf/in. (3.7 N/cm)' },
                { label: 'Protective Cap Sheet', value: 'Clear polyester' },
                { label: 'Liner', value: 'Polyethylene-coated PET' },
                {
                  label: 'Flammability',
                  value: 'Flammability standards are different from country to country. Contact a local 3M representative for details.'
                }
              ]}
            />
          </DocSection>

          <DocSection id='pb-application-characteristics' title='Application Characteristics'>
            <KeyValueTable
              rows={[
                { label: 'Substrate Type', value: 'Painted surfaces*' },
                {
                  label: 'Application Temperature',
                  value: '60F to 85F. However for optimal application, 3M recommends installing at 65F to 75F (18.3C to 23.9C).'
                },
                {
                  label: 'Application Method',
                  value: 'Wet or Dry (see the 3M™ Protection Wrap Film Installation Guide)'
                }
              ]}
            />
            <p className='text-xs text-muted'>* Check the film&rsquo;s adhesion to powder-coated or water-based paints.</p>
            <p className='text-xs text-muted'>
              NOTE: This film is NOT recommended for use on stainless steel substrates, nor warranted for stainless
              steel applications. Contact a 3M sales representative for information about more appropriate products.
            </p>
          </DocSection>

          <DocSection id='pb-factors-performance-life' title='Factors Affecting Performance Life'>
            <p>The actual performance life of a protection wrap is affected by:</p>
            <BulletList
              items={[
                'Selection and preparation of the substrate',
                'Surface texture',
                'Application methods',
                'Angle and direction of sun exposure',
                'Environmental conditions',
                'Cleaning and maintenance methods'
              ]}
            />
          </DocSection>

          <DocSection id='pb-sensors' title='Installation Over Vehicle Sensors, Radar, and Autonomous Equipment'>
            <p>
              It is the vehicle owner&rsquo;s and/or vehicle manufacturer&rsquo;s responsibility to validate the
              compatibility of 3M™ Protection Wrap Film with any and all sensors or like equipment prior to
              installation. In order to ensure such equipment functions properly, 3M recommends users NEVER apply
              film over such equipment. Always test and confirm an installation does NOT create signal disruptions or
              loss of functionality prior to full adoption.
            </p>
          </DocSection>

          <DocSection id='pb-application-installation' title='Application and Installation'>
            <p>
              In addition to other 3M bulletins specified in this document, the following bulletins provide details
              users may need to successfully apply a film:
            </p>
            <BulletList
              items={[
                '3M™ Vehicle Prep for Success Guide',
                '3M™ Protection Wrap Film Installation Guide',
                '3M™ Protection Wrap Film Vehicle Maintenance FAQ'
              ]}
            />
          </DocSection>

          <DocSection id='pb-pressure-activated-adhesive' title='Pressure-Activated Adhesive'>
            <p>The pressure activated adhesive on this film offers:</p>
            <BulletList
              items={[
                'smooth sliding into position on a substrate;',
                'fast finger tacking to check positioning; and',
                'easy snap-up and repositioning when you need it.'
              ]}
            />
            <p>The snap-up and reposition feature is lost:</p>
            <BulletList
              items={[
                'when firm pressure with a squeegee or other application tool is applied;',
                'at application temperatures above 100F (38C) even if only light finger pressure was used for tacking; or',
                'if any part of the film is removed from the original liner and reapplied to the same or another liner.'
              ]}
            />
          </DocSection>

          <DocSection id='pb-air-release-channels' title='Working with Non-Visible Air Release Channels'>
            <p>
              Air release channels are a characteristic of films with 3M™ Comply™ Adhesive Technology, allowing
              trapped air to exit through the edges of the film.
            </p>
            <BulletList
              items={[
                'The channels will be damaged if installers remove and attempt to change liners or reapply the same liner.',
                'For the best results, always work from the center of a film out to the edges to allow trapped air to escape.'
              ]}
            />
          </DocSection>

          <DocSection id='pb-maintenance-cleaning' title='Maintenance and Cleaning'>
            <p>
              Detailed vehicle maintenance and cleaning instructions are specified in the 3M™ Protection Wrap Film
              Vehicle Maintenance FAQ.
            </p>
            <BulletList
              items={[
                'The cleaner must be wet, non-abrasive, without solvents, and have a pH value between 6 and 8 (neither strongly acidic nor strongly alkaline).',
                '3M™ Perfect-It™ Show Car Paste Wax (PN39526) can be applied after film installation. Users must then regularly wash and recoat the surface going forward.',
                '3M™ Ceramic Coating (PN 39901) or 3M™ Ceramic Boost (PN39905) can be applied for additional hydrophobicity, stain resistance, and easy clean features.',
                'Do NOT use waxes containing dyes, abrasive polishes, and/or rubbing compounds. The abrasive particles will damage the film surface and result in a loss of surface quality.'
              ]}
            />
          </DocSection>

          <DocSection id='pb-removal' title='Removal'>
            <p>
              3M recommends hiring an installation professional for the removal process. Use a heat gun or steamer
              to aid removal. Remove the film at a 30 to 60 degree angle relative to the surface of the paint.
            </p>
          </DocSection>

          <DocSection id='pb-shelf-life-storage' title='Shelf Life and Storage'>
            <p className='font-medium text-foreground'>Shelf Life</p>
            <p>
              The shelf life is an indicative and maximum data, subject to many external and non-controllable
              factors. It may NEVER be interpreted as a warranty. The shelf life is three years from the date of
              manufacture on the original box.
            </p>
            <p className='font-medium text-foreground'>Storage Conditions</p>
            <BulletList
              items={[
                '60F to 85F (16C to 30C)',
                'Out of sunlight',
                'Clean, dry area',
                'Original container',
                'Bring the film to room temperature before use',
                'Unused portions of a roll should be taped tightly and stored horizontally',
                'See FAQs for additional storage considerations'
              ]}
            />
          </DocSection>

          <DocSection id='pb-health-safety' title='Health and Safety'>
            <p>
              When handling any chemical products, read the manufacturers&rsquo; container labels and the safety
              data sheets (SDS) for important health, safety, and environmental information. Follow the link on
              3M.com/SDS to obtain SDS sheets for 3M products.
            </p>
            <p>When using any equipment, always follow the manufacturer&rsquo;s instructions for safe operation.</p>
          </DocSection>

          <DocSection id='pb-warranty-disclaimer' title='Warranty, Limited Remedy, and Disclaimer'>
            <p className='text-xs text-muted'>Legal boilerplate, standard 3M language.</p>
            <p className='text-xs uppercase text-muted'>
              Unless a different warranty is specifically stated on the applicable 3M product packaging or product
              literature (in which case such warranty governs), 3M warrants that each 3M product meets the
              applicable 3M product specification at the time 3M ships the product. 3M makes no other warranties or
              conditions, express or implied, including, but not limited to, any warranty or condition of
              merchantability, fitness for a particular purpose, or arising out of a course of dealing, custom, or
              usage of trade. If a 3M product does not conform to this warranty, the sole and exclusive remedy is,
              at 3M&rsquo;s option, replacement of the 3M product or refund of the purchase price.
            </p>
            <p className='text-xs uppercase text-muted'>
              Except for the limited remedy stated above, and except to the extent prohibited by law, 3M will not be
              liable for any loss or damage arising from or related to the 3M product, whether direct, indirect,
              special, incidental, or consequential (including, but not limited to, lost profits or business
              opportunity), regardless of the legal or equitable theory asserted, including, but not limited to,
              warranty, contract, negligence, or strict liability.
            </p>
          </DocSection>
        </DisclosureGroup>
      </div>

      {/* Installation Guide */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Installation Guide</h3>
        <p className='text-xs text-muted mb-3'>3M™ Protection Wrap Film Installation Guide, Revision A, April 2024</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='ig-overview' title='Overview'>
            <p>
              This installation guide provides details on the proper installation of the 3M™ Protection Wrap Film.
              This document includes a list of recommended products and tools as well as vehicle preparation and 3M™
              Protection Wrap Film installation best practices.
            </p>
          </DocSection>

          <DocSection id='ig-tools' title='Installation Tools'>
            <BulletList
              items={[
                'WrapGlove® GHOST™ glove or similar wrap glove',
                'Scotch-Brite® lint-free microfiber cloths',
                'Cutting tools: a 30-degree stainless steel blade cutting knife; 3M™ Knifeless™ Tape Precision Line (recommended) or 3M™ Knifeless™ Tape Design Line (alternative)',
                'Felt squeegee',
                'Electric heat gun',
                'Isopropyl alcohol',
                'Deionized water',
                'Spray bottles',
                'Non-detergent soap',
                '3M™ General Purpose Adhesive Cleaner or similar adhesive remover solvent',
                '3M™ Foaming Glass Cleaner or similar non-ammonium foaming glass cleaner',
                "3M™ Perfect-It™ Cleaner Clay or Meguiar's® Mirror Glaze® Professional Detailing Clay, Mild, CS200",
                "Optional: 3M™ Quick Wax Spray Detailer or Meguiar's® Ultimate Quick Detailer",
                'Optional: 3M™ Primer 94'
              ]}
            />
          </DocSection>

          <DocSection id='ig-tips-tricks' title='Tips and Tricks'>
            <BulletList
              items={[
                'Film is flexible and conformable without heat.',
                'Heat is recommended when applying the film to deep recesses.',
                'For best results, do NOT over stretch material when conforming around curves.',
                'Post-heat edges thoroughly after installation to prevent the material from lifting over time.',
                '3M™ Knifeless™ Tape Precision Line can easily cut through the film.',
                'Pre-heating cutting blade or a heated blade can assist in cutting free floating film.'
              ]}
            />
          </DocSection>

          <DocSection id='ig-vehicle-condition' title='Vehicle Preparation: Vehicle Condition'>
            <p>
              All wrap films, including 3M™ Protection Wrap Film, should be applied to a &ldquo;like new&rdquo;
              vehicle finish for the best results. 3M&rsquo;s warranty only applies to vehicles that have an
              excellent bond between the paint and the vehicle substrate. Any rust, bubbling, scratches, dents, or
              other damage will be visible through the film, and such imperfections may damage the film or cause the
              film to fail prematurely. Areas repaired with body filler must be sanded, epoxy primed, and, for best
              results, finished with an original equipment manufacturer paint or clear coat. Any recently applied
              finish must be fully cured according to the paint manufacturer&rsquo;s directions before film is
              applied. Any defect in the paint (including scratches, dents, bubbling, pitting, fisheyes, and orange
              peel) may be visible through 3M™ Protection Wrap Film, especially when using the flat, gloss colors.
              3M recommends a professional paint correction be performed when heavy orange peeling is present on a
              vehicle prior to installing 3M™ Protection Wrap Film. Failure to do so will result in high gloss films
              taking on the orange peel appearance. Wrap shops should set proper expectations for their customers
              before installing any 3M™ Protection Wrap Film.
            </p>
          </DocSection>

          <DocSection id='ig-installation-environment' title='Vehicle Preparation: Installation Environment'>
            <p>
              Working with 3M™ Protection Wrap Film high gloss films requires a clean installation environment. Keep
              the floors clean of dust and debris to lessen the chance of contaminants getting under the film.
              Lint-free clothing and hair covers may also be necessary. Film should be installed immediately after
              the installation surface has been cleaned.
            </p>
          </DocSection>

          <DocSection id='ig-cleaning-instructions' title='Vehicle Preparation: Cleaning Instructions'>
            <p className='text-xs text-muted'>
              See the 3M™ Vehicle Prep for Success Guide for detailed cleaning guidelines. 3M strongly recommends
              following these detailed cleaning guides prior to installing any gloss color films.
            </p>
            <ol className='list-decimal space-y-1.5 pl-5 text-foreground/80'>
              <li>
                Work with an authorized paint correction specialist or automotive detailer to remove any ceramic
                coating, if one is present on the vehicle. See the Ceramic Coating Removal SOP for details.
              </li>
              <li className='text-muted'>
                Step 2 (an adhesive remover step) fell inside a garbled portion of the source PDF&rsquo;s text layer
                and could not be recovered; it is omitted rather than guessed at.
              </li>
              <li>Use 3M™ Foaming Glass Cleaner (or a similar product) to remove any excess adhesive cleaner.</li>
              <li>Clean the vehicle surface with non-detergent soap.</li>
              <li>
                Use 3M™ Perfect-It™ Cleaner Clay or Meguiar&rsquo;s® Mirror Glaze® Professional Detailing Clay, Mild,
                CS200 together with water and non-detergent soap to remove surface contaminants from the painted
                surface.
              </li>
              <li>Wipe down the vehicle with a clean Scotch-Brite® lint-free microfiber cloth.</li>
            </ol>
          </DocSection>

          <DocSection id='ig-installation-preparation' title='Vehicle Preparation: Installation Preparation'>
            <ol className='list-decimal space-y-1.5 pl-5 text-foreground/80'>
              <li>
                Optional: To minimize the risk of adhesive lines and wrinkles, apply Meguiar&rsquo;s® Ultimate Quick
                Detailer to the vehicle&rsquo;s high points, and if desired, wipe large flat areas with Meguiar&rsquo;s®
                Ultimate Quick Detailer.
              </li>
              <li>
                Optional: Apply 3M™ Primer 94 to vehicle areas where the film will be stretched, particularly on
                vehicle panel edges, to help maximize the film&rsquo;s bond to the substrate.
              </li>
              <li>Clean all edges with a microfiber cloth and a mixture of 70% isopropyl alcohol and 30% deionized water.</li>
              <li>Optional: Apply 3M™ Knifeless™ Tape Precision Line on vehicle seams as desired.</li>
              <li>Measure the vehicle wrap area.</li>
            </ol>
            <p className='font-medium text-foreground pt-2'>Additional considerations prior to installation</p>
            <BulletList
              items={[
                'Textured, metallic, pearl, and color flip films may have a directional characteristic to their appearance. For the best overall appearance, install the film in a consistent orientation through the entire installation. 3M recommends checking the film&rsquo;s orientation and color prior to installation. Do NOT alternate the film&rsquo;s orientation from panel to panel.',
                'Lot-to-lot variations are uncommon in 3M™ Protection Wrap Film colors as 3M has specifications in place to prevent large color variations. However, film colors can vary slightly from lot to lot. Use the same lot number of film for an entire vehicle to ensure a consistent appearance.'
              ]}
            />
          </DocSection>

          <DocSection id='ig-installation-instructions' title='Installation Instructions'>
            <p className='text-xs text-muted'>
              Pages 2 to 3 of the source PDF (the numbered installation-steps list and part of the maintenance,
              cleaning, and removal section) extracted as garbled text: a font-substitution/encoding artifact of the
              PDF, not readable content. That range was excluded rather than guessed at. Only steps 1 and 10 to 13,
              which extracted cleanly, are shown below; steps 2 to 9 fell inside the corrupted range and are omitted.
            </p>
            <ol className='list-decimal space-y-1.5 pl-5 text-foreground/80'>
              <li>Mist the liner and cap sheet with water to help reduce static cling.</li>
            </ol>
            <p className='text-xs text-muted my-2'>Steps 2 to 9 fell inside the corrupted extraction range and are omitted.</p>
            <ol className='list-decimal space-y-1.5 pl-5 text-foreground/80' start={10}>
              <li>
                If there are any adhesive markings, gently lift the film off the vehicle, starting with the high
                points, and use a heat gun set to 200F to 220F (93.3C to 104.4C) to fix adhesive markings.
              </li>
              <li>Use a WrapGlove® GHOST™ glove to apply film to the high points.</li>
              <li>Palm stretch the film into recesses.</li>
              <li>
                Install the flat area using the palm of the WrapGlove® GHOST™ glove or apply the film with a
                squeegee, working in 1 in. to 2 in. (2.5 cm to 5.1 cm) overlapping strokes at a 45-degree squeegee
                angle.
              </li>
            </ol>
          </DocSection>
        </DisclosureGroup>
      </div>

      {/* Warranty */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Warranty</h3>
        <p className='text-xs text-muted mb-3'>3M™ Protection Wrap Film Vehicle Owner Limited Warranty</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='w-coverage' title='Vehicle Owner Limited Warranty'>
            <p>
              3M designs, formulates and manufactures 3M™ Protection Wrap Film Color Series for optimum,
              performance and durability. The 3M commitment to quality is the foundation for the warranty coverage
              we provide.
            </p>
            <p>
              3M warrants that each 3M product meets the applicable 3M product specification at the time 3M ships
              the product. In addition, 3M warrants from the date of install/purchase:
            </p>
            <BulletList items={['10-year warranty coverage', 'No Yellowing', 'No Bubbling', 'No Cracking']} />
            <p>If the 3M Product does not conform to this warranty, the sole and exclusive remedy is:</p>
            <BulletList
              items={[
                'Replacement or refund of the purchase price of the quantity of film proved to be non-conforming.',
                'Provide removal of the non-conforming Product and, if applicable, reapplication of Product of like quality using 3M selected installers, free of charge.'
              ]}
            />
          </DocSection>

          <DocSection id='w-exclusions' title='Limited Warranty Exclusions and Limited Remedy'>
            <BulletList
              items={[
                'The following warranty is only valid in the United States and Canada.',
                'This warranty does not apply to damage caused by misuse, accident, misapplication or ordinary wear, nor does it apply to dents caused by physical impact of rocks or road debris.',
                'Warranty coverage does not include paint pull during film removal process.',
                'Warranty is limited to approved application surfaces listed in the 3M™ Protection Wrap Film Product Bulletin.',
                'Some installers may choose to offer, on their own behalf, different or additional warranties or remedies, beyond the limited warranty and limited remedy offered by 3M.'
              ]}
            />
            <p className='text-xs text-muted'>
              By entering the information into 3M&rsquo;s warranty registration system, you consent to allow 3M to
              share with Carfax the VIN number and installation date. Personal information (name, address, phone
              number, etc.) will NOT be included in the submission to Carfax.
            </p>
          </DocSection>

          <DocSection id='w-claim-process' title='Making a Warranty Claim'>
            <p>
              To make a claim under this warranty in the United States, first contact your installer. If the
              installer is not available, contact 3M at 1-800-328-1684. 3M will direct you to an installer selected
              by 3M to have your 3M™ Protection Wrap Film inspected and, if your claim is covered, 3M will have the
              installer provide the stated remedy. In Canada, first contact your installer; if not available, call
              1-800-364-3577. The warranty card must be presented along with the original invoice to receive
              warranty service. Form must be complete for warranty to be valid.
            </p>
          </DocSection>

          <DocSection id='w-implied-warranty' title='Implied Warranty Limitation'>
            <p className='text-xs uppercase text-muted'>
              All implied warranties and conditions, including but not limited to the implied warranty or condition
              of merchantability, the implied warranty or condition of fitness for a particular purpose, and any
              implied warranty or condition arising out of a course of dealing or of performance, custom or usage of
              trade, are limited in duration to the applicable warranty time period. Some states&rsquo; jurisdictions
              do not allow limitations on how long an implied warranty lasts, so the above limitation may not apply
              to you.
            </p>
          </DocSection>

          <DocSection id='w-limitation-of-liability' title='Limitation of Liability'>
            <p className='text-xs uppercase text-muted'>
              Except where prohibited by law, 3M and the installer of the 3M product shall not be liable for any
              loss or damage arising from the 3M product, whether direct, indirect, special, incidental or
              consequential, regardless of the legal theory asserted, including warranty, contract, negligence or
              strict liability. Some states do not allow the exclusion or limitation of incidental or consequential
              damages, so the above limitation or exclusion may not apply to you.
            </p>
          </DocSection>
        </DisclosureGroup>
      </div>
    </div>
  )
}
